import classNames from "classnames";
import { Immutable } from "immer";
import React from "react";
import SVG from "react-inlinesvg";
import Modal from "react-modal";
import { c, t } from "ttag";

import { interpolate } from "@thelabnyc/thelabui/src/utils/i18n";

import iconXClose from "../../../img/icons/x-close.svg";
import { WebPageURL } from "../../models/nominals";
import { listFinancingPlans } from "../../utils/financing";
import { format, formatFinancingPlan } from "../../utils/format";
import { focusElement } from "../../utils/keyboardFocus";
import { Dinero, getMonthlyPrice } from "../../utils/money";
import { usePageURL } from "../../utils/urls";
import { Link } from "../Link";

const renderPlanTable = (financeURL: WebPageURL): React.ReactNode => {
    const plans = listFinancingPlans();

    // Build table rows
    const planRows = plans.map((plan, index) => {
        const formattedPlan = formatFinancingPlan(plan);
        const moPrice = format.money(
            getMonthlyPrice(plan.threshold, plan.length),
        );
        const rowClasses = classNames({
            "card-financing-modal-table__row": true,
            "card-financing-modal-table__row--odd":
                Math.abs((index + 1) % 2) === 1,
            "card-financing-modal-table__row--even": (index + 1) % 2 === 0,
        });
        return (
            <tr key={index} className={rowClasses}>
                <th scope="row" className="card-financing-modal-table__cell">
                    {formattedPlan.threshold}
                </th>
                <td className="card-financing-modal-table__cell">
                    <Link href={financeURL}>
                        {c("CardFinancingModal")
                            .t`${formattedPlan.length} months`}
                        <sup>1</sup>
                    </Link>
                </td>
                <td className="card-financing-modal-table__cell">
                    <Link href={financeURL}>
                        {t`${moPrice}/mo`}
                        <sup>2</sup>
                    </Link>
                </td>
            </tr>
        );
    });

    return (
        <table className="card-financing-modal-table">
            <thead>
                <tr className="card-financing-modal-table__row--title">
                    <th
                        scope="col"
                        className="card-financing-modal-table__cell card-financing-modal-table__cell--title"
                    >
                        {t`With a minimum purchase of...`}
                    </th>
                    <th
                        scope="col"
                        className="card-financing-modal-table__cell card-financing-modal-table__cell--title"
                    >
                        {t`Get 0% APR for...`}
                    </th>
                    <th
                        scope="col"
                        className="card-financing-modal-table__cell card-financing-modal-table__cell--title"
                    >
                        {t`With a monthly payment of...`}
                    </th>
                </tr>
            </thead>
            <tbody>{planRows}</tbody>
        </table>
    );
};

const calculateModalStyle = (modalHeight: number): Modal.Styles => {
    const padding = 30; // adjust this to your needs
    const height = modalHeight + padding * 2;
    const heightPx = `${height}px`;
    const heightOffset = height / 2;
    const offsetPx = `${heightOffset}px`;

    return {
        content: {
            top: "50%",
            bottom: "auto",
            left: "50%",
            right: "auto",
            position: "fixed",
            transform: "translate(-50%,-" + offsetPx + ")",
            height: heightPx,
            padding: padding,
        },
    };
};

export const CardFinancingModal = (
    props: Immutable<{
        price?: Dinero;
        title: string;
        linkText: string;
    }>,
): React.ReactNode => {
    const [modalHeight, setModalHeight] = React.useState(600);
    const [modalIsOpen, setModalIsOpen] = React.useState(false);
    const modalContentRef = React.useRef<HTMLDivElement | null>(null);

    const financeLinkURL = usePageURL("finance_link");
    const homeURL = usePageURL("home");
    const financeURL = financeLinkURL || homeURL;

    const onOpenModal = (event: React.MouseEvent<HTMLElement>) => {
        if (event) {
            event.preventDefault();
        }
        setModalIsOpen(true);
    };

    const onCloseModal = (
        event: React.MouseEvent<Element> | React.KeyboardEvent<Element>,
    ) => {
        if (event) {
            event.preventDefault();
        }
        setModalIsOpen(false);
    };

    const onAfterModalOpen = () => {
        if (!modalContentRef.current) {
            return;
        }
        setModalHeight(modalContentRef.current.getBoundingClientRect().height);

        // close button is automatically focused when the modal first opens
        focusElement(".address-suggestion-model__close");
    };

    const modalStyle = calculateModalStyle(modalHeight);

    return (
        <span>
            <button
                className="product-card__installment product-card__installment--modal-link"
                onClick={onOpenModal}
            >
                {props.linkText}
            </button>
            <Modal
                aria={{ modal: true }}
                contentLabel={t`Special Financing Available`}
                className="card-financing-modal"
                isOpen={modalIsOpen}
                onAfterOpen={onAfterModalOpen}
                onRequestClose={onCloseModal}
                role="dialog"
                style={modalStyle}
            >
                <div
                    className="card-financing-modal__all-content"
                    ref={modalContentRef}
                >
                    <button
                        aria-label={t`Close`}
                        className="card-financing-modal__close"
                        onClick={onCloseModal}
                    >
                        <SVG
                            aria-hidden="true"
                            className="card-financing-modal__close-icon"
                            src={iconXClose}
                        />
                    </button>
                    <div className="card-financing-modal__content">
                        <h1 className="card-financing-modal__title">
                            {t`Special Financing Available`}
                        </h1>
                        <div className="card-financing-modal__copy-container">
                            <p
                                className="card-financing-modal__copy"
                                dangerouslySetInnerHTML={{
                                    __html: interpolate(
                                        t`Finance the %s (%s) when part of a qualifying order:`,
                                        [
                                            props.title,
                                            format.money(props.price),
                                        ],
                                    ),
                                }}
                            ></p>
                            {renderPlanTable(financeURL)}
                        </div>
                        <div className="card-financing-modal-cta-container">
                            <div className="card-financing-modal__cta-copy">
                                {t`Financing with approved credit`}
                            </div>
                            <button
                                className="button card-financing-modal__cta"
                                onClick={onCloseModal}
                            >
                                {t`Continue shopping`}
                            </button>
                        </div>
                    </div>
                </div>
            </Modal>
        </span>
    );
};
